/**
 * `/training-data` - Admin -> AI -> Training data (OCP-615, epic ORB-1952).
 *
 * What the control-plane training bucket holds: per tenant the consent
 * state (opt-in / opt-out from the relay's batch header), rows / bytes /
 * batches per operation and the newest batch; fleet totals per operation
 * and month; the export runs with their manifests and file downloads; and
 * the per-tenant purge (with an optional opt-out stamp). Counters only -
 * the rows themselves never leave the bucket except through an export.
 *
 * The Terms dependency is stated on the page: nothing here is USED for
 * training without the consent of AGB V4 (OWS-D23) 3.5 (active opt-in).
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { sensitiveReadUrl } from '../lib/sensitive-read';
import { fmtDateTime } from '../lib/format-time';

type Consent = 'optin' | 'optout' | 'unknown';

interface TenantView {
  tenant_id: string;
  subdomain: string;
  deletion_state: string | null;
  consent: Consent;
  optin_at: string | null;
  optout_at: string | null;
  rows: number;
  bytes: number;
  batches: number;
  last_batch_at: string | null;
  by_operation: Record<string, { rows: number; bytes: number; batches: number }>;
}

interface ExportView {
  id: string;
  status: string;
  requested_by: string;
  prefix: string;
  started_at: string;
  finished_at: string | null;
  rows: number;
  bytes: number;
  tenants: number;
  files: string[];
  error: string | null;
  /** OCP-774 - a byte budget cut rows from this run. */
  truncated?: boolean;
  export_limit_reached?: boolean;
  truncated_tenants?: string[];
  skipped_bytes?: number;
  limits?: { tenant_max_bytes: number; export_max_bytes: number } | null;
}

interface Overview {
  configured: boolean;
  bucket: string;
  tenants: TenantView[];
  by_operation: Record<string, { rows: number; bytes: number; tenants: number }>;
  by_month: Array<{ month: string; rows: number; bytes: number }>;
  exports: ExportView[];
}

const CONSENT_LABEL: Record<Consent, string> = { optin: 'Opted in', optout: 'Opted out', unknown: 'No signal yet' };

function consentClasses(c: Consent): string {
  if (c === 'optin') return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800';
  if (c === 'optout') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200 dark:ring-gray-700';
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function AdminTrainingDataPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<TenantView | null>(null);
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeOptOut, setPurgeOptOut] = useState(true);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await api<Overview>('/v1/admin/ai/training-data'));
    } catch (err) {
      setLoadError(err instanceof ApiError ? `Could not load training data (HTTP ${err.status}).` : 'Could not load training data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startExport = async () => {
    setExportBusy(true);
    setNotice(null);
    try {
      const res = await api<{ export: ExportView }>('/v1/admin/ai/training-exports', { method: 'POST' });
      setNotice(`Export ${res.export.id.slice(0, 8)} started - it runs as a job; refresh to see it finish.`);
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? `Could not start the export (HTTP ${err.status}).` : 'Could not start the export.');
    } finally {
      setExportBusy(false);
    }
  };

  const runPurge = async () => {
    if (!purgeTarget) return;
    setPurgeBusy(true);
    setPurgeError(null);
    try {
      const res = await api<{ deleted_count: number; remaining_count: number; opted_out: boolean }>(
        `/v1/admin/tenants/${purgeTarget.tenant_id}/training-data/purge`,
        { method: 'POST', body: { reason: purgeReason, opt_out: purgeOptOut } },
      );
      setNotice(`Purged ${res.deleted_count} object(s) for ${purgeTarget.subdomain}${res.opted_out ? ' and stamped the tenant opted out' : ''}.`);
      setPurgeTarget(null);
      setPurgeReason('');
      await load();
    } catch (err) {
      setPurgeError(err instanceof ApiError ? `Purge failed (HTTP ${err.status}).` : 'Purge failed.');
    } finally {
      setPurgeBusy(false);
    }
  };

  const ops = data ? Object.keys(data.by_operation).sort() : [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Training data</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
            Pseudonymised training rows an opted-in tenant relays land in the control-plane bucket under the
            tenant&apos;s own prefix. Only counters live here; the rows leave the bucket through an export
            only. An opt-out purges the tenant&apos;s prefix. Nothing is used for training before the Terms carry
            the opt-in clause.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => void startExport()}
            disabled={exportBusy || data?.configured === false}
            className="rounded bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            title={data?.configured === false ? 'No object store for training data' : undefined}
          >
            {exportBusy ? 'Starting…' : 'Start export'}
          </button>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {loadError}
        </div>
      )}
      {notice && (
        <div role="status" className="mt-4 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
          {notice}
        </div>
      )}
      {data && !data.configured && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          No object store is wired for training data - the intake answers 503 to every relay batch.
        </div>
      )}

      {data && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="training-totals">
            <Tile label="Tenants with data">{data.tenants.filter((t) => t.rows > 0).length}</Tile>
            <Tile label="Rows">{Object.values(data.by_operation).reduce((s, o) => s + o.rows, 0)}</Tile>
            <Tile label="Bytes">{fmtBytes(Object.values(data.by_operation).reduce((s, o) => s + o.bytes, 0))}</Tile>
            <Tile label="Bucket"><span className="font-mono text-xs">{data.bucket}</span></Tile>
          </div>

          <section className="mt-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">By operation</h2>
            {ops.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No rows stored yet.</p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="training-by-operation">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr><th className="py-1 pr-4">Operation</th><th className="py-1 pr-4">Rows</th><th className="py-1 pr-4">Bytes</th><th className="py-1 pr-4">Tenants</th></tr>
                  </thead>
                  <tbody>
                    {ops.map((op) => (
                      <tr key={op} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1 pr-4 font-mono text-xs">{op}</td>
                        <td className="py-1 pr-4">{data.by_operation[op]!.rows}</td>
                        <td className="py-1 pr-4">{fmtBytes(data.by_operation[op]!.bytes)}</td>
                        <td className="py-1 pr-4">{data.by_operation[op]!.tenants}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.by_month.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                By month: {data.by_month.map((m) => `${m.month} ${m.rows} rows`).join(' · ')}
              </p>
            )}
          </section>

          <section className="mt-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tenants</h2>
            {data.tenants.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No tenant has sent a consent signal yet.</p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="training-tenants">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="py-1 pr-4">Tenant</th><th className="py-1 pr-4">Consent</th><th className="py-1 pr-4">Rows</th>
                      <th className="py-1 pr-4">Bytes</th><th className="py-1 pr-4">Batches</th><th className="py-1 pr-4">Last batch</th><th className="py-1 pr-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.tenants.map((t) => (
                      <tr key={t.tenant_id} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1 pr-4">
                          <span className="font-medium">{t.subdomain}</span>
                          {t.deletion_state ? <span className="ml-2 text-xs text-gray-500">({t.deletion_state})</span> : null}
                          <div className="text-xs text-gray-500">{Object.entries(t.by_operation).map(([op, v]) => `${op} ${v.rows}`).join(' · ') || '-'}</div>
                        </td>
                        <td className="py-1 pr-4">
                          <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${consentClasses(t.consent)}`} title={`opt-in ${t.optin_at ? fmtDateTime(t.optin_at) : '-'} / opt-out ${t.optout_at ? fmtDateTime(t.optout_at) : '-'}`}>
                            {CONSENT_LABEL[t.consent]}
                          </span>
                        </td>
                        <td className="py-1 pr-4">{t.rows}</td>
                        <td className="py-1 pr-4">{fmtBytes(t.bytes)}</td>
                        <td className="py-1 pr-4">{t.batches}</td>
                        <td className="py-1 pr-4 text-xs">{t.last_batch_at ? fmtDateTime(t.last_batch_at) : '-'}</td>
                        <td className="py-1 pr-4 text-right">
                          <button
                            type="button"
                            onClick={() => { setPurgeTarget(t); setPurgeError(null); }}
                            className="rounded border border-red-300 dark:border-red-700 px-2 py-1 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                          >
                            Purge
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mt-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Exports</h2>
            {data.exports.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No export run yet.</p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="training-exports">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr><th className="py-1 pr-4">Started</th><th className="py-1 pr-4">Status</th><th className="py-1 pr-4">Rows</th><th className="py-1 pr-4">Tenants</th><th className="py-1 pr-4">By</th><th className="py-1 pr-4">Files</th></tr>
                  </thead>
                  <tbody>
                    {data.exports.map((e) => (
                      <tr key={e.id} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1 pr-4 text-xs">{fmtDateTime(e.started_at)}</td>
                        <td className="py-1 pr-4">
                          {e.status}
                          {e.error ? <div className="text-xs text-red-600 dark:text-red-400">{e.error}</div> : null}
                          {e.truncated ? (
                            <div className="text-xs text-amber-700 dark:text-amber-400" data-testid="training-export-truncated">
                              Truncated: {e.export_limit_reached ? 'export budget reached' : 'tenant budget'}
                              {(e.truncated_tenants?.length ?? 0) > 0 ? `, ${e.truncated_tenants!.length} tenant${e.truncated_tenants!.length === 1 ? '' : 's'} cut` : ''}
                              {`, ${fmtBytes(e.skipped_bytes ?? 0)} skipped`}
                              {e.limits ? ` (limits ${fmtBytes(e.limits.tenant_max_bytes)} per tenant, ${fmtBytes(e.limits.export_max_bytes)} per export)` : ''}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-1 pr-4">{e.rows}</td>
                        <td className="py-1 pr-4">{e.tenants}</td>
                        <td className="py-1 pr-4 text-xs">{e.requested_by}</td>
                        <td className="py-1 pr-4 text-xs">
                          {e.files.map((f) => (
                            <button
                              key={f}
                              type="button"
                              className="mr-2 font-mono text-brand-600 hover:underline"
                              onClick={() => {
                                // OCP-930 - customer workspace content in bulk: a sensitive read with a reason.
                                const url = sensitiveReadUrl(`/v1/admin/ai/training-exports/${e.id}/files/${f}`, 'This training-data file');
                                if (url) window.location.href = url;
                              }}
                            >
                              {f}
                            </button>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {purgeTarget && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Purge training data for {purgeTarget.subdomain}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Deletes every object under this tenant&apos;s prefix ({purgeTarget.rows} rows, {fmtBytes(purgeTarget.bytes)}) and its counters. Audited with your reason.
            </p>
            <label className="mt-3 block text-sm">
              <span className="text-gray-700 dark:text-gray-300">Reason (at least 10 characters)</span>
              <textarea
                value={purgeReason}
                onChange={(e) => setPurgeReason(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                data-testid="purge-reason"
              />
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={purgeOptOut} onChange={(e) => setPurgeOptOut(e.target.checked)} />
              Also stamp the tenant opted out (later relay batches are refused until the tenant opts back in)
            </label>
            {purgeError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{purgeError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setPurgeTarget(null)} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
              <button
                type="button"
                onClick={() => void runPurge()}
                disabled={purgeBusy || purgeReason.trim().length < 10}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="purge-confirm"
              >
                {purgeBusy ? 'Purging…' : 'Purge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}
